import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ContactsService } from 'src/app/core/lists';
import { StringUtilsService } from '../../../../../../../../core/services/string-utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LayoutUtilsService, MessageType } from '../../../../../../../../core/_base/crud';
export function ConfirmedValidator(controlName: string, matchingControlName: string) {
  return (formGroup: FormGroup) => {
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];
    if (matchingControl.errors && !matchingControl.errors.confirmedValidator) {
      return;
    }
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ confirmedValidator: true });
    } else {
      matchingControl.setErrors(null);
    }
  };
}
@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-reset-contact-credential',
  templateUrl: './reset-contact-credential.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetContactCredentialComponent implements OnInit, OnDestroy {
  createPasswordForm: FormGroup;
  private subscriptions: any[] = [];
  @Input() result;
  hasFormErrors = false;
  fromResetPassButton;
  error = 'Oh snap! Change a few things up and try submitting again.';

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    public stringUtils: StringUtilsService,
    public dialogRef: MatDialogRef<ResetContactCredentialComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private contactService: ContactsService,
    private layoutUtilsService: LayoutUtilsService,
  ) {}

  ngOnInit() {
    this.fromResetPassButton = this.data.resetPass;
    this.initialCreatePasswordForm();
    if (this.data.resetPass) {
      this.createPasswordForm.controls.firstField.clearValidators();
      this.createPasswordForm.controls.firstField.updateValueAndValidity();
    } else {
      this.createPasswordForm.controls.password.clearValidators();
      this.createPasswordForm.controls.password.updateValueAndValidity();
      this.createPasswordForm.controls.confirmPassword.clearValidators();
      this.createPasswordForm.controls.confirmPassword.updateValueAndValidity();
    }
  }
  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.createPasswordForm.controls[controlName];
    if (!control) {
      return false;
    }
    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  initialCreatePasswordForm() {
    this.createPasswordForm = this.fb.group(
      {
        password: new FormControl(
          '',
          Validators.compose([
            Validators.required,
            Validators.pattern(
              '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&#^])[A-Za-zd$@$!%*?&#^].{8,}',
            ),
          ]),
        ),
        confirmPassword: new FormControl('', Validators.compose([Validators.required])),
        firstField: [
          null,
          Validators.compose([
            Validators.required,
            Validators.pattern('[a-zA-z][a-zA-Z0-9._-]{5,}$'),
          ]),
        ],
      },
      {
        validator: ConfirmedValidator('password', 'confirmPassword'),
      },
    );
  }
  onSubmit() {
    this.hasFormErrors = false;
    const controls = this.createPasswordForm.controls;
    if (this.createPasswordForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      return;
    }
    if (this.data.resetPass) {
      const body = {
        password: this.createPasswordForm.controls.password.value,
        password2: this.createPasswordForm.controls.confirmPassword.value,
        username:
          typeof this.data.username === 'object'
            ? this.data.username?.username
            : this.data.username,
      };
      for (const [key, value] of Object.entries(body)) {
        if (typeof value === 'string') {
          body[key] = value.trim();
        }
      }
      this.contactService.resetPassword(body).subscribe(
        (res) => {
          this.result = res;
          if (res) {
            const message = 'Your password has been reset successfully';
            this.layoutUtilsService.showActionNotification(
              message,
              MessageType.Read,
              6000,
              true,
              false,
            );
            this.dialogRef.close();
          }
          this.cdr.detectChanges();
        },
        (err) => {
          if (err.error.phone) {
            this.error = 'cell phone must not be empty';
            this.hasFormErrors = true;
            this.cdr.detectChanges();
          }
          if (err.error.pin) {
            this.error = err.error.pin;
            this.hasFormErrors = true;
            this.cdr.detectChanges();
          }
          if (err.error.cause) {
            this.error = 'Duplicate cell phone';
            this.hasFormErrors = true;
            this.cdr.detectChanges();
          }
        },
      );
    } else {
      const body = {
        username: this.createPasswordForm.controls.firstField.value,
      };
      for (const [key, value] of Object.entries(body)) {
        if (typeof value === 'string') {
          body[key] = value.trim();
        }
      }
      this.contactService.updateUsername(body, this.data?.contactId).subscribe(
        (res) => {
          this.result = res;
          if (res) {
            const message = 'Your username has been changed successfully';
            this.layoutUtilsService.showActionNotification(
              message,
              MessageType.Read,
              6000,
              true,
              false,
            );
            this.dialogRef.close();
          }
          this.cdr.detectChanges();
        },
        (err) => {
          if (err.error.message) {
            this.createPasswordForm.controls.firstField.setErrors({ incorrect: true });
            this.createPasswordForm.controls.firstField.markAsTouched();
            this.error = err.error.message;
            this.cdr.detectChanges();
          }
        },
      );
    }
  }
  onAlertClose() {
    this.hasFormErrors = false;
  }
  onCloseClick(): void {
    this.dialogRef.close();
  }
  get f() {
    return this.createPasswordForm.controls;
  }
  getTitle(): any {
    if (this.fromResetPassButton) {
      return 'Reset Password';
    }
    return 'Change Username';
  }
}
